import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUp, Plus, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import AdminShell, { Pending } from "@/components/admin/AdminShell";
import { Md } from "@/components/admin/Md";
import { Card, Spinner } from "@/components/Common";
import { api, enc, errMsg } from "@/lib/api";
import { ADVISOR_PROMPTS, askAdvisor } from "@/lib/advisor";
import { when } from "@/lib/admin";
import { cn } from "@/lib/utils";

const Sessions = ({ items, active, busy, onOpen, onNew, onDelete }) => (
  <Card className="h-fit min-w-0 p-3" data-testid="advisor-sessions">
    <Button onClick={onNew} disabled={busy} variant="outline" className="mb-3 w-full rounded-full" data-testid="advisor-new-chat"><Plus className="mr-1 h-4 w-4" />New conversation</Button>
    {!items.length && <p className="px-2 text-xs text-zinc-500">No saved conversations yet.</p>}
    <div className="max-h-[50vh] space-y-1 overflow-y-auto">
      {items.map((s) => (
        <div key={s.session_id} className={cn("group flex items-center gap-1 rounded-lg", s.session_id === active ? "bg-[#33290D]" : "hover:bg-[#121214]")}>
          <button onClick={() => onOpen(s.session_id)} disabled={busy} className="min-w-0 flex-1 px-2.5 py-2 text-left disabled:opacity-60" data-testid={`advisor-session-${s.session_id}`}>
            <span className={cn("block truncate text-sm", s.session_id === active ? "text-[#F3C649]" : "text-zinc-300")}>{s.title || "Untitled"}</span>
            <span className="block text-[10px] text-zinc-600">{when(s.updated_at)}</span>
          </button>
          <button onClick={() => onDelete(s.session_id)} aria-label="Delete conversation" className="p-2 text-zinc-600 transition-opacity hover:text-red-400 lg:opacity-0 lg:group-hover:opacity-100" data-testid={`advisor-delete-${s.session_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
    </div>
  </Card>
);

const Bubble = ({ m }) => (
  <div className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")} data-testid={`advisor-msg-${m.role}`}>
    {m.role === "user"
      ? <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-[#D4AF37] px-4 py-2.5 text-sm text-black [overflow-wrap:anywhere]">{m.content}</div>
      : <div className="min-w-0 max-w-[92%] rounded-2xl rounded-bl-sm border border-[#27272A] bg-[#121214] px-4 py-3 [overflow-wrap:anywhere]">{m.content ? <Md text={m.content} /> : <Spinner className="text-[#D4AF37]" />}</div>}
  </div>
);

const Welcome = ({ onAsk }) => (
  <div className="py-6" data-testid="advisor-welcome">
    <div className="mb-1 flex items-center gap-2 font-display text-xl text-zinc-50"><Sparkles className="h-5 w-5 text-[#D4AF37]" />Your top advisor</div>
    <p className="mb-5 max-w-lg text-sm text-zinc-400">Ask anything about how RPOPRO is performing. Answers use your live numbers: revenue, funnel, AI costs, campaigns and leads.</p>
    <div className="grid gap-2 sm:grid-cols-2">
      {ADVISOR_PROMPTS.map((p, i) => (
        <button key={p} onClick={() => onAsk(p)} data-testid={`advisor-prompt-${i}`}
          className="rounded-xl border border-[#27272A] bg-[#121214] px-4 py-3 text-left text-sm text-zinc-300 transition-colors hover:border-[#D4AF37]/50 hover:text-zinc-50">{p}</button>
      ))}
    </div>
  </div>
);

export default function Advisor() {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  const end = useRef();
  const asked = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const loadSessions = () => api.get("/admin/advisor/sessions").then((r) => setSessions(r.data?.items || r.data || [])).catch((e) => e.response?.status === 404 && setMissing(true));
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => {
    const p = location.state?.prompt;
    if (!p || asked.current) return;
    asked.current = true;
    navigate(location.pathname, { replace: true, state: null });
    send(p);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs]);

  const open = async (id) => {
    setSid(id); setMsgs([]);
    try { const { data } = await api.get(`/admin/advisor/sessions/${enc(id)}`); setMsgs(data.messages || []); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/advisor/sessions/${enc(id)}`); if (id === sid) { setSid(null); setMsgs([]); } loadSessions(); } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    const add = (chunk) => setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], content: m[m.length - 1].content + chunk }]);
    try {
      const meta = await askAdvisor({ message, session_id: sid }, add);
      if (meta.session_id) setSid(meta.session_id);
      loadSessions();
    } catch (e) {
      if (e.status === 404) setMissing(true);
      setMsgs((m) => (m[m.length - 1]?.content ? m : m.slice(0, -2)));
      setText((t) => t || message);
      toast.error(e.status === 404 ? "The Advisor isn't live on the backend yet." : e.message);
    } finally { setBusy(false); }
  };

  return (
    <AdminShell title="AI Advisor" subtitle="Your top business advisor, grounded in live RPOPRO data.">
      {missing && <Pending />}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <Sessions items={sessions} active={sid} busy={busy} onOpen={open} onNew={() => { setSid(null); setMsgs([]); }} onDelete={remove} />
        <Card className="flex min-h-[60vh] min-w-0 flex-col p-4 sm:p-6" data-testid="advisor-chat">
          <div className="flex-1 space-y-4 overflow-y-auto" data-testid="advisor-messages">
            {msgs.length ? msgs.map((m, i) => <Bubble key={i} m={m} />) : <Welcome onAsk={send} />}
            <div ref={end} />
          </div>
          <div className="mt-4 flex items-end gap-2 rounded-2xl border border-[#27272A] bg-[#09090B] p-2 focus-within:border-[#D4AF37]/50">
            <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask about revenue, funnel, margins, leads..."
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              className="max-h-40 min-h-[40px] resize-none border-0 bg-transparent focus-visible:ring-0" data-testid="advisor-input" />
            <Button onClick={() => send()} disabled={busy || !text.trim()} size="icon" className="h-10 w-10 shrink-0 rounded-full" data-testid="advisor-send">{busy ? <Spinner /> : <ArrowUp className="h-4 w-4" />}</Button>
          </div>
          <p className="mt-2 text-[11px] text-zinc-600">Advice is generated by AI from your business data. Double-check before acting on big decisions.</p>
        </Card>
      </div>
    </AdminShell>
  );
}
